/**
 * @fileoverview Shared types for the file-upload feature.
 *
 * Hidden behind the `fileUpload` feature flag. These types describe the
 * pre-send representation of a file that the user has attached to the chat
 * input. The feature is skeleton-only today — see the `TODO`s in the
 * components/utils for the real upload + validation wiring.
 */

/**
 * Lifecycle state of a single attached file in the pre-send chat input.
 *
 * - `UPLOADING`: bytes are being transferred (chip shows a spinner).
 * - `READY`: upload finished successfully (chip rendered in normal colors).
 * - `ERROR`: upload/validation failed (chip shows a red border + message and
 *   must be acknowledged/removed by the user before a message can be sent).
 */
export enum UploadFileStatus {
  UPLOADING = "uploading",
  READY = "ready",
  ERROR = "error",
}

/**
 * Pre-send model of a file attached to the chat input.
 *
 * Holds both the UI-facing metadata (name/size/type/status) used to render the
 * chip and the underlying browser {@link File} so the bytes can be POSTed to
 * S3 on send. After a successful upload, `s3Key` records the final object key
 * the file was written to.
 */
export interface UploadFile {
  /** Stable client-generated id used as the React key and removal handle. */
  id: string;
  /** Original filename as reported by the browser. */
  name: string;
  /** File size in bytes. */
  size: number;
  /** MIME type as reported by the browser (may be empty for unknown types). */
  type: string;
  /** Current lifecycle state of the attachment. */
  status: UploadFileStatus;
  /** Human-readable error text, present only when `status === ERROR`. */
  errorMessage?: string;
  /**
   * True when the chip's only problem is exceeding the per-message file limit.
   * Recomputed on every list change (unlike sticky per-file errors), so it
   * clears once the list is back within the limit. See `reconcileCountErrors`.
   */
  isCountError?: boolean;
  /**
   * The underlying browser File. Present for valid, user-selected attachments
   * and used to build the multipart POST body when uploading to S3. May be
   * absent in tests that construct lightweight UI-only fixtures.
   */
  file?: File;
  /**
   * Final S3 object key the file was written to. Populated only after a
   * successful upload (status transitions to READY following the POST).
   */
  s3Key?: string;
}

/**
 * A successfully-uploaded attachment, paired with the message it was sent
 * with. Built from a {@link UploadFile} once its upload completes (so `s3Key`
 * is guaranteed present) and carried alongside the prompt to the backend so
 * the agent knows exactly which files accompanied this turn — rather than
 * having to discover them blindly. Also persisted on the chat {@link Message}
 * so the conversation history can render the sent files as read-only badges.
 */
export interface SentAttachment {
  /** Original filename as reported by the browser. */
  name: string;
  /** File size in bytes (for the history badge). */
  size: number;
  /** MIME type as reported by the browser (for the history badge icon). */
  type: string;
  /** Final S3 object key the file was written to (the full `projects/.../` key). */
  s3Key: string;
}

/**
 * Builds the {@link SentAttachment} list from the pre-send files after a
 * successful upload. Only READY files that actually recorded an `s3Key` are
 * included; anything still pending/errored (which would have aborted the send)
 * is defensively skipped.
 */
export function toSentAttachments(files: UploadFile[]): SentAttachment[] {
  return files
    .filter((f) => f.status === UploadFileStatus.READY && Boolean(f.s3Key))
    .map((f) => ({
      name: f.name,
      size: f.size,
      type: f.type,
      s3Key: f.s3Key as string,
    }));
}
